import React, { useContext, useMemo, useState } from 'react';
import Cookies from 'js-cookie';

import SocketContext, { useSocket } from 'utils/hooks/useSocket';
import { useTournament } from 'context/TournamentContext';
import { useWalletData } from 'context/WalletDataContext';
import { formatCurrency, formatPrice } from 'utils';
import { CASH_TICKER, pairLabelOf } from 'utils/cash';
import PairPicker from 'components/game/PairPicker';
import { PairTrigger } from 'components/game/PairPicker/styled';

import {
  Hero,
  SingleRow,
  SpreadPillMobile,
  PriceCell,
  Price,
  PriceUnit,
  MicroStats,
  StatCell,
  PnlGroup,
  PnlCell,
} from './styled';

const TradeHero = () => {
  const [pickerOpen, setPickerOpen] = useState(false);

  const { tournamentDetails, tournamentPairList } = useTournament();
  const { tournamentRanking } = useSocket() as any;
  const { selectedCoin, selectedCoinValue, setCoin } = useContext(SocketContext) as any;
  const { totalMoneyString } = useWalletData();

  const rawData: any = tournamentDetails?.data;
  const attributes = rawData?.attributes ?? rawData?.data ?? rawData;
  const tournamentType = attributes?.trade_type;
  const initialMoney = Number(attributes?.amount_start) || 1;

  const username = localStorage.getItem('username') ?? Cookies.get('username') ?? '';
  const rankingArray = Array.isArray(tournamentRanking) ? tournamentRanking : [];
  const userRanking = rankingArray.find((entry: any) => entry?.username === username);
  const totalUSDT =
    tournamentType === 'Margin'
      ? userRanking
        ? userRanking.totalInUSDT
        : parseFloat(totalMoneyString)
      : parseFloat(totalMoneyString);
  const totalUSDTSafe = Number.isFinite(totalUSDT) ? totalUSDT : 0;
  const sessionPnL = totalUSDTSafe - initialMoney;
  const sessionPctRaw = ((totalUSDTSafe - initialMoney) / initialMoney) * 100;
  const sessionPct = Number.isFinite(sessionPctRaw) ? sessionPctRaw : 0;
  const isPos = sessionPnL >= 0;
  const sessionClass = isPos ? 'tb-pos' : 'tb-neg';

  const activeSym = useMemo(() => {
    const fromName =
      typeof selectedCoin?.name === 'string' ? selectedCoin.name.split('/')[0] : undefined;
    return (fromName ?? selectedCoin?.short_name ?? selectedCoin?.base?.data?.attributes?.short_name ?? '') as string;
  }, [selectedCoin]);
  const activeSymUpper = String(activeSym || '').toUpperCase();

  const last = (selectedCoinValue as any)?.last;
  const change = (selectedCoinValue as any)?.percentage;
  const high24 = (selectedCoinValue as any)?.high;
  const low24 = (selectedCoinValue as any)?.low;
  const hasPrice = typeof last === 'number' && Number.isFinite(last) && last > 0;
  const hasChange = typeof change === 'number' && Number.isFinite(change);
  const hasHigh = typeof high24 === 'number' && Number.isFinite(high24) && high24 > 0;
  const hasLow = typeof low24 === 'number' && Number.isFinite(low24) && low24 > 0;
  const changeClass = hasChange ? (change >= 0 ? 'tb-pos' : 'tb-neg') : '';

  const handlePick = (data: any) => {
    setCoin(data);
    setPickerOpen(false);
  };

  const pairLabel = pairLabelOf(activeSymUpper, selectedCoin?.name);

  return (
    <>
      <Hero>
        <SingleRow>
          <PairTrigger type="button" onClick={() => setPickerOpen(true)}>
            <span className="ico">
              {activeSymUpper ? (
                <img
                  src={`/icons/tradingPair/use/${activeSymUpper}.svg`}
                  alt={activeSymUpper}
                  onError={(e) => {
                    (e.target as HTMLElement).style.display = 'none';
                  }}
                />
              ) : (
                '?'
              )}
            </span>
            {pairLabel || 'Select pair'}
            <span className="chev" aria-hidden>▼</span>
          </PairTrigger>
          {hasChange && (
            <SpreadPillMobile>
              24h
              <b className={changeClass}>
                {change >= 0 ? '+' : ''}
                {change.toFixed(2)}%
              </b>
            </SpreadPillMobile>
          )}
          <PriceCell>
            <Price>{hasPrice ? formatPrice(activeSymUpper, last) : '—'}</Price>
            <PriceUnit>
              {hasChange && (
                <span className="pct">
                  24h
                  <b className={changeClass}>
                    {change >= 0 ? '+' : ''}
                    {change.toFixed(2)}%
                  </b>
                </span>
              )}
              <span className="unit">{CASH_TICKER}</span>
            </PriceUnit>
          </PriceCell>
          {(hasHigh || hasLow) && (
            <MicroStats>
              <StatCell>
                <div className="l">24H High</div>
                <div className="v">{hasHigh ? formatPrice(activeSymUpper, high24) : '—'}</div>
              </StatCell>
              <StatCell>
                <div className="l">24H Low</div>
                <div className="v">{hasLow ? formatPrice(activeSymUpper, low24) : '—'}</div>
              </StatCell>
            </MicroStats>
          )}
          <PnlGroup>
            <PnlCell>
              <div className="l">Funds</div>
              <div className="v">{formatCurrency(totalUSDTSafe)}</div>
            </PnlCell>
            <PnlCell>
              <div className="l">P&amp;L Result</div>
              <div className={`v ${sessionClass}`}>
                {isPos ? '+' : ''}
                {sessionPct.toFixed(2)}%
              </div>
            </PnlCell>
          </PnlGroup>
        </SingleRow>
      </Hero>
      <PairPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={handlePick}
        activeSym={activeSymUpper}
        pairsList={tournamentPairList || []}
      />
    </>
  );
};

export default TradeHero;
